import * as React from "react";
import { isFtthOffer, useOffer } from "@nju/data-access-portal-offers";
import { IFtthOrchestrator } from "@nju/data-access-portal-orchestrator";
import { Panel, Separator, Text } from "@nju/ui";
import { getAvailableActions } from "../utils";
import { FtthStatusBannerDetails } from "./FtthStatusBannerDetails";
import { getFtthStatusData } from "./getFtthStatusData";
import { AdditionalInfo } from "./partials/AdditionalInfo";

export interface IFtthStatusBannerProps {
  orchestrator: IFtthOrchestrator;
}

export function FtthStatusBanner({ orchestrator }: IFtthStatusBannerProps) {
  const {
    actions,
    contactNumberWithoutCountyCode,
    installationToken,
    installationAddress,
    note,
    appointmentTimeSlot,
    state,
    cancellationReason,
    offerId,
    userId,
    orderSummary,
  } = orchestrator;

  const activationFee =
    orderSummary?.items?.find((item) => {
      return item.offerId === "P-NJU-FTTH-Activation-Payment";
    })?.offerPrice ?? 0;

  const offer = useOffer(offerId);

  const panelProps = React.useMemo(() => {
    return getFtthStatusData(state, cancellationReason);
  }, [cancellationReason, state]);

  const { ORDER_CANCEL, APPOINTMENT_SLOT_CHANGE_FULFILLMENT } =
    getAvailableActions(actions);

  if (!panelProps) {
    // eslint-disable-next-line unicorn/no-null
    return null;
  }

  const { text, title, iconName, showAdditionalInfo } = panelProps;

  return (
    <>
      <Panel
        iconName={iconName}
        isFoldable={!!offer}
        testId="ftth-order-status-banner"
        title={title}
      >
        {({ isExpanded }) => {
          return isExpanded ? (
            <>
              <Text
                size={{
                  "@onlyMobile": "m",
                  "@fromTablet": "l",
                }}
              >
                {text}
              </Text>
              {isFtthOffer(offer) && (
                <FtthStatusBannerDetails
                  activationFee={activationFee}
                  allowAppointmentDateChange={
                    APPOINTMENT_SLOT_CHANGE_FULFILLMENT
                  }
                  allowCancelOrder={ORDER_CANCEL}
                  appointmentTimeSlot={appointmentTimeSlot}
                  contactNumber={contactNumberWithoutCountyCode}
                  installationToken={installationToken}
                  note={note}
                  offer={offer}
                  sfh={installationAddress?.isSfh}
                  status={title}
                  userId={userId}
                />
              )}
            </>
          ) : (
            <Text
              size={{
                "@onlyMobile": "m",
                "@fromTablet": "l",
              }}
            >
              {text}
            </Text>
          );
        }}
      </Panel>
      {showAdditionalInfo && (
        <>
          <Separator size="xs" tone="invisible" />
          <AdditionalInfo />
          <Separator size="xs" tone="invisible" />
        </>
      )}
    </>
  );
}
